import clsx from 'clsx'
import { Folder, Document, AlertErrorCircle, Retry, Clock } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { EmailAttachmentCount } from '@/user_files/components/EmailAttachmentCount'
import { kEmailAttachmentNameIndentPx } from '@/user_files/components/email-attachment-indent'

export type DriveListItemVariant = 'folder' | 'file'

export interface DriveListItemProps {
  /** Folder or file */
  variant: DriveListItemVariant
  /** Status */
  status?: DriveListItemStatus
  /** Name */
  name: string
  /** Visual nesting for an extracted attachment within the current folder. */
  nameIndentLevel?: number
  attachmentParentName?: string
  /**
   * When true, shows parent email context as visible metadata (search / flat results).
   * Browse tree rows keep parent context in the accessible name only.
   */
  showAttachmentParentLabel?: boolean
  /** Extracted attachment count shown as number + paperclip between the filename and disclosure. */
  attachmentCount?: number
  /** Chevron-only expand/collapse control rendered immediately after the filename. */
  disclosure?: React.ReactNode
  /** Formatted date segment. */
  formattedDate: string
  /** Formatted time segment. */
  formattedTime: string
  /** Secondary details for the row (e.g. "24 KB" or "4 files"). */
  detailsLabel?: string
  /** Containing folder for the row. Provided only in search results, where the Location column exists. */
  locationLabel?: string
  /** Whole trail from the drive root, revealed on hover since the cell only fits the parent. */
  locationPath?: string
  /** Why this row cannot be selected, shown on hover over its inert checkbox. */
  selectionDisabledReason?: string
  /** Shown below name when status is Failed (with icon). */
  errorMessage?: string
  /** Non-error status detail shown below the name. */
  statusMessage?: string
  /** Disable the checkbox. */
  disabled?: boolean
  /** Whether the item is selected. */
  selected?: boolean
  /** On toggle select */
  onToggleSelect?: () => void
  /** Whether the row has a selection checkbox at all. Defaults to true. */
  selectable?: boolean
  /**
   * Keeps the selection cell but leaves it empty, for a row that cannot be selected in a table
   * where others can. Dropping the cell instead would shift every following cell one column left.
   */
  selectionPlaceholder?: boolean
  /** On retry */
  onRetry?: () => void
  /** On click */
  onClick?: () => void
  /** Optional icon override for file rows. */
  fileIcon?: React.ReactNode
  /** Optional icon override for folder rows. */
  folderIcon?: React.ReactNode
  /** Optional metadata shown below the item name in narrow tables and immediately after it in wider tables. */
  nameMetadata?: React.ReactNode
  /** Overflow menu */
  overflowMenu: React.ReactNode
}

const PLACEHOLDER_VALUE = '--'

/**
 * Shared table row for drive list items (folders and files).
 * Supports folder/file variants and pending, queued, processing, completed, and failed states.
 */
export function DriveListItem({
  variant,
  status = DriveListItemStatus.Completed,
  name,
  nameIndentLevel = 0,
  attachmentParentName,
  showAttachmentParentLabel = false,
  attachmentCount,
  disclosure,
  formattedDate,
  formattedTime,
  detailsLabel,
  locationLabel,
  locationPath,
  selectionDisabledReason,
  errorMessage,
  statusMessage,
  disabled = false,
  selected,
  onToggleSelect,
  selectable = true,
  selectionPlaceholder = false,
  onRetry,
  onClick,
  fileIcon,
  folderIcon,
  nameMetadata,
  overflowMenu,
}: DriveListItemProps) {
  const isQueued = status === DriveListItemStatus.Queued
  const isLoading = status === DriveListItemStatus.Processing || status === DriveListItemStatus.Pending || isQueued
  const isError = status === DriveListItemStatus.Failed
  const displayDate = isLoading ? PLACEHOLDER_VALUE : formattedDate
  const displayTime = isLoading ? '' : formattedTime
  const isRowInteractive = typeof onClick === 'function'

  /**
   * Every row gets the same wrapper, tooltip or not: the span participates in the cell's inline
   * layout, so rendering it for some rows and not others made disabled checkboxes sit a few pixels
   * off from their neighbours. Only the tooltip is conditional.
   */
  const renderSelectionControl = () => {
    const showReason = disabled && Boolean(selectionDisabledReason)
    const control = (
      <span className={clsx('inline-flex align-middle', disabled && 'cursor-not-allowed')} data-testid={showReason ? 'selection-disabled-reason' : undefined}>
        <input
          type="checkbox"
          checked={selected && !disabled}
          onChange={disabled ? undefined : onToggleSelect}
          disabled={disabled}
          className={clsx(
            'size-5 rounded border-brand-neutral-300 text-brand-500 align-middle',
            disabled ? 'cursor-not-allowed disabled:bg-brand-neutral-100' : 'cursor-pointer focus:ring-brand-500',
            // A disabled control emits no mouse events, so the hover has to land on the wrapper.
            showReason && 'pointer-events-none'
          )}
          aria-label={`Select ${name}`}
        />
      </span>
    )

    if (!showReason) return control

    return (
      <Tooltip title={selectionDisabledReason ?? ''} placement="top">
        {control}
      </Tooltip>
    )
  }

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isRowInteractive || e.target !== e.currentTarget) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  const renderIcon = () => {
    if (isQueued) {
      return <Clock className="h-6 w-6 shrink-0 text-brand-neutral-600" role="img" aria-label="Queued" />
    }
    if (isLoading) {
      return <CircularProgressContinuousSized size={24} thickness={5} showTrack className="shrink-0" />
    }
    if (variant === 'folder') {
      return folderIcon ?? <Folder className="w-6 h-6 shrink-0 text-brand-neutral-950" />
    }
    if (fileIcon) return fileIcon
    return <Document className="w-6 h-6 shrink-0 text-brand-neutral-950" />
  }

  return (
    <tr
      className={clsx(
        'h-[54px] border-t border-brand-neutral-200 first:border-t-0',
        isRowInteractive && 'cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
        isError ? 'bg-red-50' : 'hover-capable:hover:bg-brand-neutral-100'
      )}
      onClick={onClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={isRowInteractive ? 0 : undefined}
    >
      {selectable && (
        <td
          className="w-12 align-middle pl-4 pr-3 md:pl-2"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {selectionPlaceholder ? null : renderSelectionControl()}
        </td>
      )}
      <td className={clsx('align-middle pr-6', selectable ? 'pl-0' : 'pl-4 md:pl-2')}>
        <div
          className="relative flex items-center gap-2 min-w-0"
          style={nameIndentLevel ? { paddingLeft: `${nameIndentLevel * kEmailAttachmentNameIndentPx}px` } : undefined}
        >
          {renderIcon()}
          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
            {/* Parent label stays under the title until 1024px so Name + Last Modified don't fight for width. */}
            <div className="flex min-w-0 flex-col items-start gap-0.5 [@container_(min-width:_1024px)]:flex-row [@container_(min-width:_1024px)]:items-center [@container_(min-width:_1024px)]:gap-2">
              <div className="flex w-full min-w-0 flex-col items-start gap-0.5 [@container_(min-width:_640px)]:flex-row [@container_(min-width:_640px)]:items-center [@container_(min-width:_640px)]:gap-2 [@container_(min-width:_1024px)]:w-auto">
                <span className="flex w-full min-w-0 items-center gap-x-1.5 [@container_(min-width:_640px)]:w-auto">
                  <span className={clsx('min-w-0 truncate', isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-950')} title={name}>
                    {name}
                  </span>
                  {attachmentCount != null && attachmentCount > 0 ? <EmailAttachmentCount count={attachmentCount} /> : null}
                  {disclosure}
                </span>
                {nameMetadata}
              </div>
              {attachmentParentName ? (
                showAttachmentParentLabel ? (
                  <span className="max-w-full truncate text-xs text-brand-neutral-600" title={`Attachment of ${attachmentParentName}`}>
                    Attachment of {attachmentParentName}
                  </span>
                ) : (
                  <span className="sr-only">Attachment of {attachmentParentName}</span>
                )
              ) : null}
            </div>
            {isError && errorMessage && (
              <div className="flex items-center gap-1 min-w-0">
                <AlertErrorCircle className="w-3.5 h-3.5 shrink-0 text-red-800" aria-hidden />
                <span className="text-xs text-red-800 truncate" title={errorMessage}>
                  {errorMessage}
                </span>
              </div>
            )}
            {!isError && statusMessage && (
              <span className="truncate text-xs text-brand-neutral-600" title={statusMessage}>
                {statusMessage}
              </span>
            )}
          </div>
        </div>
      </td>
      <td
        className={clsx(
          'hidden min-w-0 overflow-hidden px-6 align-middle [@container_(min-width:_640px)]:table-cell',
          isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-800'
        )}
      >
        {displayDate || displayTime ? (
          <span className="block truncate tabular-nums" title={[displayDate, displayTime].filter(Boolean).join(' ')}>
            {[displayDate, displayTime].filter(Boolean).join(' ')}
          </span>
        ) : null}
      </td>
      <td
        className={clsx(
          'hidden min-w-0 overflow-hidden px-6 align-middle [@container_(min-width:_768px)]:table-cell',
          isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-800'
        )}
      >
        <span className="block truncate tabular-nums" title={detailsLabel ?? PLACEHOLDER_VALUE}>
          {detailsLabel ?? PLACEHOLDER_VALUE}
        </span>
      </td>
      {locationLabel !== undefined && (
        <td
          className={clsx(
            'hidden min-w-0 overflow-hidden px-6 align-middle [@container_(min-width:_768px)]:table-cell',
            isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-800'
          )}
        >
          {locationPath ? (
            <Tooltip title={locationPath} placement="top">
              <span className="block truncate">{locationLabel}</span>
            </Tooltip>
          ) : (
            <span className="block truncate" title={locationLabel}>
              {locationLabel}
            </span>
          )}
        </td>
      )}
      <td
        className="w-12 pl-2 pr-2 align-middle"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="flex min-h-7 min-w-7 items-center justify-end">
          {isError && onRetry && (
            <IconButton
              type="button"
              variant="plain"
              color="neutral"
              size="sm"
              icon={<Retry className="w-5 h-5" />}
              aria-label="Retry"
              onClick={(e) => {
                e.stopPropagation()
                onRetry()
              }}
            />
          )}
        </div>
      </td>
      <td
        className="pl-0 pr-2 align-middle md:pr-0"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="flex min-h-7 min-w-7 items-center justify-end">{overflowMenu}</div>
      </td>
    </tr>
  )
}
